import { useCallback, useState } from 'react';
import { ref, push, set, get, remove } from 'firebase/database';
import { useAuth } from './useAuth';
import { Feedback } from '../types';
import { database } from "@/firebase/firebase.ts";

export const useFeedback = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();

    const submitFeedback = async (feedbackData: {
        type: 'bug' | 'feature' | 'general' | 'ui';
        rating: number;
        subject: string;
        message: string;
    }) => {
        if (!user) {
            throw new Error('User must be authenticated to submit feedback');
        }

        setLoading(true);
        setError(null);

        try {
            const feedbackRef = ref(database, 'feedback');
            const newFeedbackRef = push(feedbackRef);

            const feedback: Omit<Feedback, 'id'> = {
                userId: user.uid,
                userEmail: user.email || '',
                userDisplayName: user.displayName || 'Anonymous',
                type: feedbackData.type,
                rating: feedbackData.rating,
                subject: feedbackData.subject,
                message: feedbackData.message,
                timestamp: Date.now(),
                status: 'new',
            };

            await set(newFeedbackRef, feedback);

            return newFeedbackRef.key;
        } catch (err) {
            console.error('Error submitting feedback:', err);
            setError('Failed to submit feedback. Please try again.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const getUserFeedback = async (): Promise<Feedback[]> => {
        if (!user) return [];

        try {
            const feedbackRef = ref(database, 'feedback');
            const snapshot = await get(feedbackRef);

            if (snapshot.exists()) {
                const feedbackData = snapshot.val();
                return Object.entries(feedbackData)
                    .filter(([_, feedback]: [string, any]) => feedback.userId === user.uid)
                    .map(([id, feedback]: [string, any]) => ({
                        id,
                        ...feedback
                    }))
                    .sort((a, b) => b.timestamp - a.timestamp);
            }

            return [];
        } catch (err) {
            console.error('Error fetching user feedback:', err);
            return [];
        }
    };

    // Admin only functions.
    // Stable identity so consumers can safely list it as an effect dependency.
    const getAllFeedback = useCallback(async (): Promise<Feedback[]> => {
        try {
            const feedbackRef = ref(database, 'feedback');
            const snapshot = await get(feedbackRef);

            if (snapshot.exists()) {
                const feedbackData = snapshot.val();
                return Object.entries(feedbackData)
                    .map(([id, feedback]: [string, any]) => ({
                        id,
                        ...feedback
                    }))
                    .sort((a, b) => b.timestamp - a.timestamp);
            }

            return [];
        } catch (err) {
            console.error('Error fetching all feedback:', err);
            return [];
        }
    }, []);

    const deleteFeedback = async (feedbackId: string) => {
        setLoading(true);
        setError(null);

        try {
            const feedbackRef = ref(database, `feedback/${feedbackId}`);
            await remove(feedbackRef);
        } catch (err) {
            console.error('Error deleting feedback:', err);
            setError('Failed to delete feedback. Please try again.');
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        submitFeedback,
        getUserFeedback,
        getAllFeedback,
        deleteFeedback,
        loading,
        error
    };
};